/*
 *  -----------------------------------------------------------------------
 *    <copyright file="automation-test-helpers" company="Triumph Research Intelligence">
 *        Copyright (C) 2024 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { RefObject } from 'react';
import { FormComponentValidity } from '@progress/kendo-react-common';
import { DialogHandle } from '@progress/kendo-react-dialogs';
import { GridHandle } from '@progress/kendo-react-grid';
import { NumericTextBoxHandle } from '@progress/kendo-react-inputs';
import _ from 'lodash';

export interface ComponentRef {
    show: boolean;
    setShow: (show: boolean) => void;
    element: Element;
    validity?: FormComponentValidity;
}

export type NullableComponentRef = ComponentRef | null;

export const setAttribute = (
    componentRef: RefObject<NullableComponentRef> | RefObject<NumericTextBoxHandle> | RefObject<DialogHandle | null>,
    attr: string,
    value: string,
) => {
    if (componentRef && componentRef.current && componentRef.current.element) {
        componentRef.current.element.setAttribute(`${attr}`, `${value}`);
    }
};

export const textToId = (text: string, fallbackIndex?: number) => {
    if (!text) {
        return fallbackIndex?.toString() ?? '';
    }

    const lastStringPartAfterColon = text.split(':').pop();

    if (!lastStringPartAfterColon && (fallbackIndex ?? -1) >= 0) {
        return fallbackIndex?.toString() ?? '';
    }

    return (lastStringPartAfterColon ?? text)
        .toLocaleLowerCase()
        .replaceAll(' ', '-')
        .replace(/[^0-9a-z_-]/gi, '');
};

export const GRID_ROW_TEST_ID_MASTER = 'master-row';
export const GRID_ROW_TEST_ID_DETAIL = 'detail-row';

export const addDetailRowIdToGrid = (gridRef: RefObject<GridHandle | null>) => {
    // Get all rows in the table
    const rows = gridRef.current?.element?.getElementsByTagName('tr');
    let previousRowWasAMasterRow = false;

    // Loop through them all...
    _.forEach(rows, (row: HTMLTableRowElement) => {
        const testId = row.getAttribute('data-testid');
        if (testId === GRID_ROW_TEST_ID_MASTER) {
            // If the row is a "master-row", we can say we've started to encounter rows
            // from the main table.
            previousRowWasAMasterRow = true;
            return;
        }

        if (!previousRowWasAMasterRow) {
            // We want to skip any rows that don't immediately follow a "master-row"
            return;
        }

        row.setAttribute('data-testid', GRID_ROW_TEST_ID_DETAIL);
        previousRowWasAMasterRow = false;
    });
};
